import { useCallback, useMemo, useState } from "react";

import type { EnvironmentId, ProjectId, ThreadEnvMode } from "@cmmd-forge/contracts";

import { readEnvironmentApi } from "~/environmentApi";
import { newCommandId } from "~/lib/utils";
import {
  countIndexedThreadsForCheckout,
  resolveCheckoutOptions,
  resolveCheckoutRemoval,
  resolveGroupWritability,
  resolveSelectedCheckout,
  type CheckoutOption,
  type CheckoutRemoval,
  type GroupWritability,
} from "~/projectCheckoutOptions";
import {
  resolveProjectDefaultModelState,
  type ProjectDefaultModelPair,
  type ProjectDefaultModelState,
} from "~/projectDefaultModel";
import { resolveCloudGroupMembers, type CloudCatalogRow } from "~/projectGroupCloudMembers";
import { useProjectIconResolver } from "~/components/projects/useProjectIconResolver";
import type { ProjectCanonicalIdentity } from "~/components/ProjectIcon";
import { canonicalRepoKeyOf } from "~/projectGroupRoute";
import { resolveEnvironmentLabels } from "~/projectGroupEnvironmentLabels";
import { DEFAULT_PROJECT_GROUPING_MODE, type ProjectGroupingMode } from "~/projectGrouping";
import { useProjectGroupingStore } from "~/projectGroupingStore";
import type { ProjectGroupingRowState } from "~/components/projects/projectGroupSettingsRows";
import { resolveProjectGroupMembers } from "~/projectGroupMembers";
import {
  resolveProjectThreadEnvMode,
  type ProjectThreadEnvModeState,
} from "~/projectThreadEnvMode";
import { useSettings } from "~/hooks/useSettings";
import { planGroupMetaUpdate, type GroupMetaUpdate } from "~/projectGroupUpdate";
import { planProjectGroupDeletion, type ProjectGroupDeletionPlan } from "~/projectGroupDeletion";
import {
  createRemovableForgeProject,
  removeSidebarProject,
} from "~/components/sidebar/sidebarProjectRemoval";
import { applyGroupMetaUpdate } from "~/projectGroupUpdateRunner";
import { useCmmdForgeRuntimeListSnapshot } from "~/cmmd/cmmdForgeRuntimeListResource";
import { useServerProviders } from "~/rpc/serverState";
import { useSavedEnvironmentRuntimeStore } from "~/environments/runtime/savedEnvironmentRuntimeStore";
import { useStore } from "~/store";
import type { Project } from "~/types";
import { useProjectsAcrossEnvironments } from "./useProjectsAcrossEnvironments";
import type { ProjectGroupRemovalTarget } from "~/projectGroupRemovalRunner";
import {
  removeProjectGroupCheckoutTarget,
  removeProjectGroupTarget,
  removeSelectedProjectGroupCheckout,
} from "~/projectGroupRemovalActions";
import { resolveProjectGroupThreadCounts } from "~/projectGroupThreadCounts";

export interface ProjectGroupSettingsState {
  readonly name: string;
  readonly checkouts: readonly CheckoutOption[];
  readonly selectedCheckoutValue: string;
  readonly threadCount: number;
  readonly threadCountByCheckoutValue: ReadonlyMap<string, number>;
  readonly defaultModel: ProjectDefaultModelState;
  readonly removal: CheckoutRemoval;
  readonly writability: GroupWritability;
  readonly threadEnvMode: ProjectThreadEnvModeState;
  readonly canonicalIdentity: ProjectCanonicalIdentity | undefined;
  readonly deletion: ProjectGroupDeletionPlan;
  readonly grouping: ProjectGroupingRowState;
  readonly onSelectCheckout: (value: string) => void;
  readonly onRename: (name: string) => void;
  /** `null` clears the project default, restoring per-machine inheritance. */
  readonly onChangeDefaultModel: (selection: ProjectDefaultModelPair | null) => void;
  /** `null` unpins the project, restoring the app-wide setting. */
  readonly onChangeThreadEnvMode: (mode: ThreadEnvMode | null) => void;
  readonly onDeleteProject: () => Promise<void>;
  readonly onRemoveCheckoutTarget: (target: ProjectGroupRemovalTarget) => Promise<void>;
  readonly onRemoveProjectTarget: (target: ProjectGroupRemovalTarget) => Promise<void>;
  readonly onChangeGrouping: (mode: ProjectGroupingMode) => void;
  readonly onRemoveCheckout: () => Promise<void>;
}

/**
 * Send one planned update to the connection that owns that checkout.
 *
 * The one place brands are re-applied: everything in the planner is a plain
 * string so those decisions stay testable without minting brands.
 */
async function dispatchGroupMetaUpdate(update: GroupMetaUpdate): Promise<void> {
  const api = readEnvironmentApi(update.environmentId as EnvironmentId);
  // A connection that is not loaded here cannot be written to. Throwing rather
  // than skipping is deliberate: skipping would report success while leaving
  // that machine holding the old value.
  if (!api) throw new Error(`No connection for ${update.environmentId}`);
  await api.orchestration.dispatchCommand({
    type: "project.meta.update",
    commandId: newCommandId(),
    projectId: update.projectId as ProjectId,
    ...(update.patch.title === undefined ? {} : { title: update.patch.title }),
    ...(update.patch.defaultModelSelection === undefined
      ? {}
      : { defaultModelSelection: update.patch.defaultModelSelection }),
    ...(update.patch.defaultThreadEnvMode === undefined
      ? {}
      : { defaultThreadEnvMode: update.patch.defaultThreadEnvMode }),
  });
}

/**
 * Every checkout in the group: the connected ones, plus the sprites CMMD knows
 * about that nothing on this machine has connected to.
 *
 * Decision 3 of the repo-first model — sprites join the group. A cloud catalog
 * row knows its repository and path, but the sidebar snapshot parks both inside
 * `preConnectCloud`, so a sprite never joined its own repo group. That is why
 * forge-cloud, itself a connection, reported "No checkout on this machine" for
 * a project whose sprite was registered and running.
 */
function mergeGroupMembers(
  groupKey: string,
  projects: readonly Project[],
  catalogRows: readonly CloudCatalogRow[],
) {
  const connected = resolveProjectGroupMembers(groupKey, projects);
  // A sprite that is ALSO connected already appears above with a real project
  // row and a real default; the catalog copy would be a second checkout for one
  // working copy.
  const connectedEnvironments = new Set<string>(connected.map((member) => member.environmentId));
  return [
    ...connected,
    ...resolveCloudGroupMembers(groupKey, catalogRows).filter(
      (member) => !connectedEnvironments.has(member.environmentId),
    ),
  ];
}

/**
 * Everything the group settings page needs, assembled from the store.
 *
 * The page itself stays presentational: every decision here is a call into a
 * tested pure function, because the parts that are easy to get wrong — which
 * checkouts belong to the group, which threads belong to a checkout, which
 * members a change must reach, whether a stored model pair is runnable here —
 * are exactly the parts a component test would not catch.
 */
/**
 * Which checkouts belong to this group, and what each one is called.
 *
 * Split from the settings hook because these are the reads: they answer "what
 * is this group" from three sources — the connected projects, the CMMD cloud
 * catalog, and the saved runtime registry that names the machines — and none of
 * them need to know that a settings page exists.
 */
function useProjectGroupCheckouts(groupKey: string) {
  const projects = useProjectsAcrossEnvironments();
  const savedRuntimeById = useSavedEnvironmentRuntimeStore((state) => state.byId);
  const cloudCatalog = useCmmdForgeRuntimeListSnapshot();

  const members = useMemo(
    () => mergeGroupMembers(groupKey, projects, cloudCatalog.rows),
    [groupKey, projects, cloudCatalog.rows],
  );

  const environmentLabelById = useMemo(
    () => resolveEnvironmentLabels(savedRuntimeById, members),
    [savedRuntimeById, members],
  );

  const checkouts = useMemo(
    () => resolveCheckoutOptions(members, environmentLabelById),
    [members, environmentLabelById],
  );

  return { members, checkouts };
}

/**
 * The grouping choice for this group's repository.
 *
 * Its own hook because it answers a different question from everything else on
 * the page: not "what is this project" but "how do I want my own sidebar
 * arranged". It reads a per-device store rather than the project rows, and it
 * writes nowhere near the fan-out the other settings use.
 */
function useProjectGroupGrouping(groupKey: string) {
  // Derived from the GROUP KEY, not from a member. A cloud member carries no
  // repository identity of its own, so a cloud-only group would otherwise be
  // told it has no repository to key the choice on -- when the group key is
  // precisely that repository. `canonicalRepoKeyOf` returns null for a
  // repo-less group, which is the case the row hides for.
  const canonicalKey = canonicalRepoKeyOf(groupKey);
  const groupingOverrides = useProjectGroupingStore((state) => state.overrides);
  const setGroupingMode = useProjectGroupingStore((state) => state.setMode);
  const grouping = useMemo(
    (): ProjectGroupingRowState => ({
      mode: canonicalKey
        ? (groupingOverrides[canonicalKey] ?? DEFAULT_PROJECT_GROUPING_MODE)
        : DEFAULT_PROJECT_GROUPING_MODE,
      available: canonicalKey !== null,
    }),
    [canonicalKey, groupingOverrides],
  );
  const onChangeGrouping = useCallback(
    (mode: ProjectGroupingMode) => {
      if (canonicalKey) setGroupingMode(canonicalKey, mode);
    },
    [canonicalKey, setGroupingMode],
  );

  return { grouping, onChangeGrouping };
}

/**
 * What new threads in this group start with: a model, and a workspace.
 *
 * Both are group-scoped and both are read from `members[0]`, which is the same
 * reason `name` is: every member holds the same value, so the first is as good
 * as any. Kept together because they are one question asked twice, and apart
 * from the rest because they are the only settings the page can WRITE.
 */
function useProjectGroupDefaults(
  members: ReadonlyArray<{
    readonly defaultModelSelection?: ProjectDefaultModelPair | null;
    readonly defaultThreadEnvMode?: ThreadEnvMode | null;
  }>,
  appThreadEnvMode: ThreadEnvMode,
) {
  const serverProviders = useServerProviders();

  const defaultModel = useMemo(
    () =>
      resolveProjectDefaultModelState(members[0]?.defaultModelSelection ?? null, serverProviders),
    [members, serverProviders],
  );

  const threadEnvMode = useMemo(
    () => resolveProjectThreadEnvMode(members[0]?.defaultThreadEnvMode, appThreadEnvMode),
    [members, appThreadEnvMode],
  );

  return { defaultModel, threadEnvMode };
}

export function useProjectGroupSettings(groupKey: string): ProjectGroupSettingsState {
  // NOT `useStore(selectThreadsAcrossEnvironments)`. That selector builds a new
  // array on every call, so its reference changes each render and the component
  // re-renders forever — React error #185, which took this page down in
  // production. Selecting the raw map and reading its existing index cannot.
  const environmentStateById = useStore((state) => state.environmentStateById);
  const [selectedCheckoutValue, setSelectedCheckoutValue] = useState("");
  const appThreadEnvMode = useSettings<ThreadEnvMode>((settings) => settings.defaultThreadEnvMode);

  const { members, checkouts } = useProjectGroupCheckouts(groupKey);
  // The app's own icon ladder, not a second one: every other surface that draws
  // a project identity resolves it through here.
  const resolveProjectIcon = useProjectIconResolver();
  const first = members[0];
  const canonicalIdentity = first ? resolveProjectIcon(first.environmentId, first.id) : undefined;

  const selected = resolveSelectedCheckout(checkouts, selectedCheckoutValue);
  const { defaultModel, threadEnvMode } = useProjectGroupDefaults(members, appThreadEnvMode);

  const removal = useMemo(
    () => resolveCheckoutRemoval(checkouts, selectedCheckoutValue),
    [checkouts, selectedCheckoutValue],
  );

  // `readEnvironmentApi` is the same lookup the dispatcher does, so the rows
  // are disabled by exactly the condition that would have made the write a
  // no-op rather than by a parallel guess at it.
  const writability = useMemo(
    () =>
      resolveGroupWritability(checkouts, (id) => readEnvironmentApi(id as EnvironmentId) !== null),
    [checkouts],
  );

  const threadCount = countIndexedThreadsForCheckout(environmentStateById, selected);
  const { byCheckoutValue: threadCountByCheckoutValue, total: projectThreadCount } =
    resolveProjectGroupThreadCounts(environmentStateById, checkouts);
  const deletion = useMemo(
    () => planProjectGroupDeletion(members, projectThreadCount),
    [members, projectThreadCount],
  );

  const { grouping, onChangeGrouping } = useProjectGroupGrouping(groupKey);

  const actions = useProjectGroupActions(members, removal, deletion);

  return {
    name: members[0]?.name ?? "",
    checkouts,
    selectedCheckoutValue: selected?.value ?? "",
    threadCount,
    threadCountByCheckoutValue,
    defaultModel,
    removal,
    writability,
    threadEnvMode,
    canonicalIdentity,
    deletion,
    grouping,
    onChangeGrouping,
    onSelectCheckout: setSelectedCheckoutValue,
    ...actions,
  };
}

/**
 * The three writes this page can make, kept together and away from the reads.
 *
 * Extracted because the settings hook had grown past the complexity gate: the
 * reads answer "what is this group", the writes answer "change it", and only
 * the writes need to know about dispatch, brands and rollback.
 */
function useProjectGroupActions(
  members: ReadonlyArray<{
    readonly environmentId: string;
    readonly id: string;
    readonly name: string;
    readonly defaultThreadEnvMode?: ThreadEnvMode | null;
  }>,
  removal: CheckoutRemoval,
  deletion: ProjectGroupDeletionPlan,
) {
  const onRename = useCallback(
    (name: string) => {
      const trimmed = name.trim();
      // An empty name is not a rename — `title` is a non-empty string in the
      // command contract, so sending it would be rejected per checkout and
      // leave the group half-written.
      if (trimmed.length === 0) return;
      void applyGroupMetaUpdate(
        planGroupMetaUpdate(members, { title: trimmed }),
        dispatchGroupMetaUpdate,
      );
    },
    [members],
  );

  const onChangeDefaultModel = useCallback(
    (selection: ProjectDefaultModelPair | null) => {
      void applyGroupMetaUpdate(
        planGroupMetaUpdate(members, { defaultModelSelection: selection }),
        dispatchGroupMetaUpdate,
      );
    },
    [members],
  );

  const onChangeThreadEnvMode = useCallback(
    (mode: ThreadEnvMode | null) => {
      void applyGroupMetaUpdate(
        planGroupMetaUpdate(members, { defaultThreadEnvMode: mode }),
        dispatchGroupMetaUpdate,
      );
    },
    [members],
  );

  const onRemoveCheckoutTarget = useCallback(removeProjectGroupCheckoutTarget, []);

  const onRemoveCheckout = useCallback(
    () => removeSelectedProjectGroupCheckout(removal),
    [removal],
  );

  const onDeleteProject = useCallback(async () => {
    if (!deletion.deletable) return;
    // Delegated, never reimplemented. `removeSidebarProject` owns per-step
    // progress and the CMMD archive-then-delete cascade; a second path here
    // would strand links. Passing every target in one call is what makes this
    // remove the project everywhere.
    //
    // `destroyRuntime: false` is the safety property. An Environment hosts many
    // Projects, so ending it because one was removed would delete a machine the
    // others are still running on. That is Settings → Connections.
    await removeSidebarProject(createRemovableForgeProject(deletion.targets), {
      destroyRuntime: false,
    });
  }, [deletion]);

  const onRemoveProjectTarget = useCallback(removeProjectGroupTarget, []);

  return {
    onRename,
    onChangeDefaultModel,
    onChangeThreadEnvMode,
    onRemoveCheckout,
    onDeleteProject,
    onRemoveCheckoutTarget,
    onRemoveProjectTarget,
  };
}
